import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { auctionAPI } from '../utils/api';
import { pixelEvents } from '../utils/pixelEvents';
import SEO from '../components/SEO';
import config from '../config/config';
import './Auction.css';

const Auction = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState({});

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      const response = await auctionAPI.getAll();
      setAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBidChange = (id, value) => {
    setBidAmount((prev) => ({ ...prev, [id]: value }));
  };

  const placeBid = async (auctionId) => {
    const bid = bidAmount[auctionId];
    if (!bid || bid <= 0) {
      alert('Please enter a valid bid amount.');
      return;
    }

    try {
      await auctionAPI.placeBid(auctionId, { amount: bid });
      pixelEvents.placeBid('Auction Item', auctionId, bid);
      alert('Bid placed successfully!');
      fetchAuctions();
    } catch (error) {
      console.error('Error placing bid:', error);
      alert('Failed to place bid. Please try again.');
    }
  };

  const auctionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Art Auctions - Canvaart",
    "description": "Bid on exclusive original canvas paintings in our online art auctions. Abstract art, portraits, landscapes, and more up for auction.",
    "url": "https://canvaart.in/auctions",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Canvaart",
      "url": "https://canvaart.in"
    }
  };

  return (
    <div className="auction-page">
      <SEO 
        title="Art Auctions"
        description="Bid on exclusive original canvas paintings in our online art auctions. Abstract art, portraits, landscapes, and more by Aanishka Chowdhury."
        keywords="art auction, bid on paintings, online art auction, buy paintings auction, canvas art auction, original artwork auction"
        url="/auctions"
        schema={auctionSchema}
      />
      
      <header className="auction-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Auction Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Place your bids on exclusive auctioned paintings
          </motion.p>
        </div>
      </header>

      <section className="container" aria-label="Auction listings">
        {loading ? (
          <div className="spinner" role="status" aria-label="Loading auctions"></div>
        ) : auctions.length === 0 ? (
          <div className="no-auctions">
            <p>No auctions available at the moment.</p>
          </div>
        ) : (
          <div className="auctions-grid" role="list">
            {auctions.map((auction, index) => (
              <motion.article
                key={auction.id}
                className="auction-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                role="listitem"
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="auction-image">
                  <img
                    src={`${config.BASE_API_URL}${auction?.image_url}`}
                    alt={`${auction.title} - Painting up for auction`}
                    loading="lazy"
                    itemProp="image"
                  />
                </div>
                <div className="auction-info">
                  <h3 itemProp="name">{auction.title}</h3>
                  <p className="auction-category">{auction.category}</p>
                  <p className="auction-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                    Starting Price: <span itemProp="priceCurrency">INR</span> <span itemProp="price">₹{auction.starting_price}</span>
                  </p>
                  <p className="auction-highest-bid">
                    Highest Bid: ₹{auction.highest_bid || auction.starting_price}
                  </p>
                  <div className="auction-bid">
                    <input
                      type="number"
                      placeholder="Enter your bid"
                      value={bidAmount[auction.id] || ''}
                      onChange={(e) =>
                        handleBidChange(auction.id, e.target.value)
                      }
                      aria-label={`Enter bid amount for ${auction.title}`}
                    />
                    <button
                      className="btn-bid"
                      onClick={() => placeBid(auction.id)}
                      aria-label={`Place bid on ${auction.title}`}
                    >
                      Place Bid
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Auction;